import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useRole } from "../../hooks/useRole";
import {
  CAMPAIGN_NAME_LABEL,
  ISSUER_LEGAL_NAME_LABEL,
  FUND_MANAGER_LABEL,
  REGISTERED_ADDRESS_LABEL,
  NOTICE_EMAIL_LABEL,
  ISSUER_JURISDICTION_LABEL,
  ISSUER_IDENTITY_EDIT_ROLES,
  MIN_REASON_LENGTH,
  isFundCampaign,
  campaignDisplayName,
  orUnset,
  validateIssuerIdentityEdit,
  buildIssuerIdentityPayload,
  issuerIdentityUrl,
} from "./campaignIdentity";

/**
 * IssuerIdentityPanel - who issues this offering, on the screen where it is
 * approved, and the one audited correction staff may make (R10, 2026-10).
 *
 * WHY IT EXISTS. The review screen showed corporation_legal_name as "Company
 * Name" and nowhere beside the series name, and the only way to correct a
 * fund's legal issuer or manager (prod 823/824 were stored transposed) was SQL.
 * PUT /admin/campaign/:id/issuer-identity now does it with a reason and a
 * before/after audit row.
 *
 * ACCESS. The API decides: requireRole('AD', 'CCO'). The button is shown to
 * the same two seats so nobody else is offered a control that would 403, but
 * the UI's role source (the login response) can disagree with the API's (the
 * canonical assignments) while a flag is dark, so a 403 is rendered as the
 * server's refusal and never treated as a success. The gate is
 * useRole().hasRole, deliberately: it carries the legacy Super Admin wildcard
 * because authJWT.requireRole still honours that wildcard on its legacy (dark)
 * path, so the UI offers exactly what that server accepts; on the canonical
 * path the server refuses it and the refusal is shown.
 *
 * WHY AN INLINE FORM, not a SweetAlert HTML form: Swal is mocked in every
 * suite here, so its inputs could never be driven by a test, and the page's
 * loading overlay is deliberately not used (it swallows clicks on Swal).
 *
 * On success the page's campaign is re-fetched (onChanged) rather than the
 * response trusted: its shape is not part of the contract.
 */

const Row = ({ label, children }) => (
  <div className="detailItem">
    <span className="itemKey">{label}:</span>
    <span className="itemValue">{children}</span>
  </div>
);

const fieldStyle = { display: "block", width: "100%", maxWidth: 480, marginTop: 4, padding: 6 };

function refusalMessage(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 403) {
    return (
      data.message ||
      "Only the Platform Manager (AD) or the CCO can change the issuer identity. The server refused this change."
    );
  }
  return data.message || data.code || data.error || err?.message || "Could not save the issuer identity.";
}

export default function IssuerIdentityPanel({ campaign, campaignId, onChanged, editable = true }) {
  const { hasRole } = useRole();
  const fund = isFundCampaign(campaign);
  const mayEdit = editable && hasRole(...ISSUER_IDENTITY_EDIT_ROLES);

  const [open, setOpen] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [managerName, setManagerName] = useState("");
  // W3b: the other columns the claimed listing agreement binds.
  const [registeredAddress, setRegisteredAddress] = useState("");
  const [noticeEmail, setNoticeEmail] = useState("");
  const [issuerJurisdiction, setIssuerJurisdiction] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // The DISPLAYED campaign is the write target: the form is prefilled from it,
  // and during an in-app navigation the route id moves on before the store
  // does, so the route id could name a campaign these values never came from.
  const id = campaign?.id ?? campaignId;

  // A PUT can finish after the admin has moved to another campaign (or left
  // the page). Its completion must not reload, or re-open errors over, a
  // campaign it was not about: the captured reloadCampaign would re-fetch the
  // OLD id into the store while the URL names the new one, and the next edit
  // would then pair one campaign's values with the other's id.
  const currentId = useRef(id);
  currentId.current = id;
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);
  const latestOnChanged = useRef(onChanged);
  latestOnChanged.current = onChanged;

  // A form opened for one campaign never lingers over another's data.
  useEffect(() => {
    setOpen(false);
    setError(null);
  }, [id]);

  const startEdit = () => {
    setLegalName(campaign?.corporation_legal_name || "");
    setManagerName(campaign?.fund_manager_name || "");
    setRegisteredAddress(campaign?.registered_address || "");
    setNoticeEmail(campaign?.camp_email || "");
    setIssuerJurisdiction(campaign?.issuer_jurisdiction || "");
    setReason("");
    setError(null);
    setOpen(true);
  };

  const cancel = () => {
    setOpen(false);
    setError(null);
  };

  const save = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const input = {
      campaign,
      legalName,
      managerName,
      reason,
      registeredAddress,
      noticeEmail,
      ...(fund ? { issuerJurisdiction } : {}),
    };
    const refusal = validateIssuerIdentityEdit(input);
    if (refusal) {
      setError(refusal);
      return;
    }
    const submittedId = id;
    const stillHere = () => alive.current && String(currentId.current) === String(submittedId);
    setSaving(true);
    setError(null);
    try {
      await axios.put(
        issuerIdentityUrl(process.env.REACT_APP_API, submittedId),
        buildIssuerIdentityPayload(input),
        { headers: { "x-access-token": localStorage.getItem("accessToken") } }
      );
      // The write happened either way, so the admin is always told, and told
      // which campaign it was about.
      Swal.fire({
        title: "Issuer identity updated",
        text: `Campaign ${submittedId}: the change and its reason were recorded in the audit log.`,
        icon: "success",
        confirmButtonText: "OK",
      });
      if (stillHere()) {
        setOpen(false);
        if (latestOnChanged.current) latestOnChanged.current();
      }
    } catch (err) {
      const message = refusalMessage(err);
      if (stillHere()) {
        setError(message);
      } else {
        Swal.fire({
          title: `Issuer identity for campaign ${submittedId} was not saved`,
          text: message,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } finally {
      if (alive.current) setSaving(false);
    }
  };

  const reasonLength = reason.trim().length;

  return (
    <>
      <Row label={CAMPAIGN_NAME_LABEL}>{orUnset(campaignDisplayName(campaign))}</Row>
      <Row label={ISSUER_LEGAL_NAME_LABEL}>{orUnset(campaign?.corporation_legal_name)}</Row>
      {fund ? <Row label={FUND_MANAGER_LABEL}>{orUnset(campaign?.fund_manager_name)}</Row> : null}
      <Row label={REGISTERED_ADDRESS_LABEL}>{orUnset(campaign?.registered_address)}</Row>
      <Row label={NOTICE_EMAIL_LABEL}>{orUnset(campaign?.camp_email)}</Row>
      {fund ? <Row label={ISSUER_JURISDICTION_LABEL}>{orUnset(campaign?.issuer_jurisdiction)}</Row> : null}

      {/* flexBasis 100%: the host .profile-info is a flex-wrap row of 33%
          .detailItem cells, so the action and the form take a row of their own
          instead of squeezing into one cell. */}
      {mayEdit && !open ? (
        <div style={{ flexBasis: "100%", margin: "0 0 12px" }}>
          <button type="button" className="editButton" onClick={startEdit}>
            Edit issuer identity
          </button>
        </div>
      ) : null}

      {mayEdit && open ? (
        <form
          onSubmit={save}
          aria-label="Edit issuer identity"
          style={{
            flexBasis: "100%",
            boxSizing: "border-box",
            margin: "0 0 16px",
            padding: 12,
            border: "1px solid #ddd",
            borderRadius: 6,
          }}
        >
          <div style={{ marginBottom: 10 }}>
            <label htmlFor={`issuer-legal-name-${id}`}>{ISSUER_LEGAL_NAME_LABEL}</label>
            <input
              id={`issuer-legal-name-${id}`}
              type="text"
              maxLength={255}
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              style={fieldStyle}
            />
          </div>
          {fund ? (
            <div style={{ marginBottom: 10 }}>
              <label htmlFor={`issuer-fund-manager-${id}`}>{FUND_MANAGER_LABEL}</label>
              <input
                id={`issuer-fund-manager-${id}`}
                type="text"
                maxLength={255}
                value={managerName}
                onChange={(e) => setManagerName(e.target.value)}
                style={fieldStyle}
              />
            </div>
          ) : null}
          <div style={{ marginBottom: 10 }}>
            <label htmlFor={`issuer-registered-address-${id}`}>{REGISTERED_ADDRESS_LABEL}</label>
            <input
              id={`issuer-registered-address-${id}`}
              type="text"
              maxLength={255}
              value={registeredAddress}
              onChange={(e) => setRegisteredAddress(e.target.value)}
              style={fieldStyle}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label htmlFor={`issuer-notice-email-${id}`}>{NOTICE_EMAIL_LABEL}</label>
            <input
              id={`issuer-notice-email-${id}`}
              type="text"
              maxLength={255}
              value={noticeEmail}
              onChange={(e) => setNoticeEmail(e.target.value)}
              style={fieldStyle}
            />
          </div>
          {fund ? (
            <div style={{ marginBottom: 10 }}>
              <label htmlFor={`issuer-jurisdiction-${id}`}>{ISSUER_JURISDICTION_LABEL}</label>
              <input
                id={`issuer-jurisdiction-${id}`}
                type="text"
                maxLength={255}
                value={issuerJurisdiction}
                onChange={(e) => setIssuerJurisdiction(e.target.value)}
                style={fieldStyle}
              />
            </div>
          ) : null}
          <div style={{ marginBottom: 10 }}>
            <label htmlFor={`issuer-identity-reason-${id}`}>Reason for the change</label>
            <textarea
              id={`issuer-identity-reason-${id}`}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={fieldStyle}
            />
            <small>
              {reasonLength}/{MIN_REASON_LENGTH} characters minimum. Recorded in the audit log with the
              before and after values.
            </small>
          </div>
          {error ? (
            <p role="alert" style={{ color: "#b00020", margin: "6px 0" }}>
              {error}
            </p>
          ) : null}
          <button type="submit" className="editButton" disabled={saving} aria-label="Save issuer identity">
            {saving ? "Saving..." : "Save"}
          </button>{" "}
          <button type="button" className="editButton" onClick={cancel} disabled={saving}>
            Cancel
          </button>
        </form>
      ) : null}
    </>
  );
}
